/**
 * `2b` — Configurações de FinOps: as contas AWS integradas ao CUDOS.
 *
 * Vive DENTRO do módulo, e não em Administração — BR-26: configuração POR
 * CLIENTE mora no módulo; só a global fica em Administração.
 *
 * 🔴 **A regra que dá forma a esta tela.** Pedido explícito do Eduardo na sessão
 * de frontend: quando a conectividade falha, mostrar a MENSAGEM REAL que a AWS
 * devolveu, e não um ícone vermelho mudo. Por isso a linha em falha ocupa três
 * linhas de texto — o erro cru, a ação sugerida e o botão de reverificar — em
 * vez de um badge e nada mais.
 */
import { useState } from 'react'

import type { ContaAws } from '@/api/contracts/contaAws'
import { useCadastrarConta, useContasAws, useVerificarConta } from '@/api/queries/finops'
import { mensagemDoErro } from '@/components/erros'
import { EsqueletoDeTabela } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { Badge, Botao, Campo, Entrada } from '@/components/primitivos'
import { formatarDataHoraDeInstante, formatarDataPorExtenso } from '@/format'
import { useClienteAtual } from '@/tenant/useClienteAtual'

export function ConfiguracoesFinops() {
  const idCliente = useClienteAtual()
  const consulta = useContasAws(idCliente)
  const cadastrar = useCadastrarConta(idCliente)

  const [adicionando, setAdicionando] = useState(false)
  const [accountId, setAccountId] = useState('')
  const [apelido, setApelido] = useState('')

  const podeEditar = consulta.data?.podeEditar ?? false

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="type-h4 text-text-primary">Contas AWS integradas (CUDOS)</h2>
        {podeEditar && (
          <Botao variante="accent" icone="plus" onClick={() => setAdicionando((a) => !a)}>
            adicionar conta
          </Botao>
        )}
      </div>

      {adicionando && (
        <form
          className="border-border-subtle bg-surface-card rounded-card flex flex-wrap items-end gap-4 border p-5"
          onSubmit={(evento) => {
            evento.preventDefault()
            cadastrar.mutate(
              { accountId, apelido },
              {
                onSuccess: () => {
                  setAccountId('')
                  setApelido('')
                  setAdicionando(false)
                },
              },
            )
          }}
        >
          <Campo
            rotulo="Account ID"
            obrigatorio
            hint="12 dígitos, com os zeros à esquerda"
            className="min-w-52"
          >
            <Entrada
              value={accountId}
              inputMode="numeric"
              maxLength={12}
              placeholder="214463019985"
              onChange={(e) => setAccountId(e.target.value.replace(/\D/g, ''))}
            />
          </Campo>
          <Campo rotulo="Apelido" obrigatorio className="min-w-64">
            <Entrada
              value={apelido}
              placeholder="Produção — sistemas judiciais"
              onChange={(e) => setApelido(e.target.value)}
            />
          </Campo>
          <Botao variante="primaria" type="submit" disabled={cadastrar.isPending}>
            {cadastrar.isPending ? 'salvando…' : 'salvar conta'}
          </Botao>
          {cadastrar.isError && (
            <p role="alert" className="text-danger-700 basis-full type-sm">
              {/* A mensagem do servidor. No caso de conta duplicada ela é
                  deliberadamente genérica — dizer em qual cliente a conta já
                  existe revelaria a existência de outro tenant. */}
              {mensagemDoErro(cadastrar.error)}
            </p>
          )}
        </form>
      )}

      {consulta.isPending && (
        <EsqueletoDeTabela rotulo="Carregando contas…" colunas={4} linhas={3} />
      )}
      {consulta.isError && (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      )}

      {consulta.data && consulta.data.itens.length === 0 && (
        <EstadoVazio
          titulo="Nenhuma conta AWS cadastrada para este cliente."
          descricao="Sem uma conta integrada, o Cost Explorer e o Discovery ficam sem dados. Cadastre pelo Account ID."
        />
      )}

      {consulta.data && consulta.data.itens.length > 0 && (
        <div className="border-border-subtle bg-surface-card rounded-card overflow-x-auto border">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-border-subtle border-b">
                {[
                  'Account ID',
                  'Apelido',
                  'Conectividade',
                  'Última leitura (parquet / Athena)',
                ].map((rotulo) => (
                  <th
                    key={rotulo}
                    scope="col"
                    className="text-text-muted px-3 py-3 text-left type-eyebrow"
                  >
                    {rotulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {consulta.data.itens.map((conta) => (
                <LinhaDeConta
                  key={conta.accountId}
                  conta={conta}
                  idCliente={idCliente}
                  podeEditar={podeEditar}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function LinhaDeConta({
  conta,
  idCliente,
  podeEditar,
}: {
  conta: ContaAws
  idCliente: string
  podeEditar: boolean
}) {
  // 🔴 Um `useVerificarConta` POR LINHA, e não um só compartilhado pela tabela
  // inteira — antes disso, clicar "tentar conexão novamente" numa conta desabilitava
  // o botão de TODAS as outras, e não havia como saber qual verificação estava em
  // andamento. Achado real, 14/10/2026: o usuário clicava e não tinha nenhum sinal
  // de que algo tinha acontecido — a causa era exatamente esta.
  const verificar = useVerificarConta(idCliente)

  return (
    <tr className="border-border-subtle border-b last:border-b-0">
      <td className="text-text-primary px-3 py-4 align-top type-sm font-semibold whitespace-nowrap">
        {conta.accountId}
      </td>
      <td className="text-text-body px-3 py-4 align-top type-sm">{conta.apelido}</td>
      <td className="px-3 py-4 align-top">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <Badge
              tom={
                conta.statusConectividade === 'ok'
                  ? 'sucesso'
                  : conta.statusConectividade === 'falha'
                    ? 'perigo'
                    : 'neutro'
              }
            >
              {conta.statusConectividade === 'nao_verificado'
                ? 'não verificado'
                : conta.statusConectividade}
            </Badge>
            {podeEditar && (
              <button
                type="button"
                title={verificar.isPending ? 'verificando…' : 'tentar conexão novamente'}
                aria-label={`Tentar conexão novamente na conta ${conta.accountId}`}
                disabled={verificar.isPending}
                onClick={() => verificar.mutate(conta.accountId)}
                className="text-icon-default hover:text-iox-teal duration-fast disabled:opacity-50"
              >
                <i
                  className={`ph ph-arrows-clockwise ${verificar.isPending ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
              </button>
            )}
            {/* Único jeito de saber, enquanto a chamada está em voo, que o clique
                surtiu efeito — sem isto, um clique numa conta que já estava "ok" e
                segue "ok" depois é indistinguível de não ter acontecido nada. */}
            {verificar.isPending && (
              <span role="status" className="text-text-muted type-sm">
                verificando…
              </span>
            )}
          </div>

          {/* 🔴 A MENSAGEM CRUA da AWS, e não um rótulo genérico.
              Pedido explícito do Eduardo: mostrar o erro de verdade. */}
          {conta.mensagemErro && (
            <p className="text-danger-700 max-w-lg type-sm">
              {conta.codigoErro && (
                <strong className="font-semibold">{conta.codigoErro}: </strong>
              )}
              {conta.mensagemErro}
            </p>
          )}

          {/* A sub-linha "o que fazer". Vem `null` quando o código
              de erro é desconhecido — orientação errada mandaria o
              arquiteto investigar o lugar errado. */}
          {conta.acaoSugerida && (
            <p className="text-text-muted max-w-lg type-sm">{conta.acaoSugerida}</p>
          )}

          {/* A falha da CHAMADA em si (rede/5xx) — diferente de `conta.mensagemErro`,
              que é o resultado JÁ PERSISTIDO da última verificação. Por linha, e não
              no rodapé da tela: no rodapé não dava pra saber qual conta tinha falhado. */}
          {verificar.isError && (
            <p role="alert" className="text-danger-700 max-w-lg type-sm">
              {mensagemDoErro(verificar.error)}
            </p>
          )}

          <p className="text-text-muted type-sm">
            {conta.verificadoEm
              ? `verificado em ${formatarDataHoraDeInstante(conta.verificadoEm)}`
              : 'nunca verificado'}
          </p>
        </div>
      </td>
      <td className="text-text-body px-3 py-4 align-top type-sm">
        {conta.ultimaLeituraParquet ? (
          <>
            {formatarDataPorExtenso(conta.ultimaLeituraParquet.slice(0, 10))}
            <span className="text-text-muted"> · batch diário</span>
            {conta.ultimaParticaoParquet && (
              <span className="text-text-muted block">{conta.ultimaParticaoParquet}</span>
            )}
          </>
        ) : (
          <span className="text-text-muted">nunca lida</span>
        )}
      </td>
    </tr>
  )
}
